import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  ChevronLeft,
  Ticket,
  Loader2,
  Minus,
  Plus,
  CreditCard,
  Phone,
  CheckCircle,
  AlertCircle,
  Trophy,
  ExternalLink,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Live Badge
const LiveBadge = () => (
  <span className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold uppercase tracking-wider bg-red-500 text-white rounded-full animate-pulse">
    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></span>
    LIVE
  </span>
);

// Format Gambian phone number for display
const formatPhoneDisplay = (value) => {
  const cleaned = value.replace(/[^\d]/g, '');
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 7) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
  return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 7)}`;
};

// Get raw phone number for API (with 220 prefix)
const getRawPhoneNumber = (formatted) => {
  const cleaned = formatted.replace(/[^\d]/g, '');
  if (cleaned.startsWith('220')) return cleaned;
  return `220${cleaned}`;
};

// Validate Gambian phone number
const isValidGambianPhone = (phone) => {
  const raw = phone.replace(/[^\d]/g, '');
  // Should be 7 digits (local) or 10 digits (with 220)
  if (raw.length === 7) {
    return ['2', '3', '5', '7', '9'].includes(raw[0]);
  }
  if (raw.length === 10 && raw.startsWith('220')) {
    return ['2', '3', '5', '7', '9'].includes(raw[3]);
  }
  return false;
};

export default function FixtureDetailPage() {
  const { fixtureId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, token } = useAuth();
  
  const [fixture, setFixture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  useEffect(() => {
    fetchFixture();
  }, [fixtureId]);

  // Pre-fill phone number from user profile
  useEffect(() => {
    if (user?.phone && !phoneNumber) {
      const cleaned = user.phone.replace(/[^\d]/g, '');
      // Remove 220 prefix if present
      const local = cleaned.startsWith('220') ? cleaned.slice(3) : cleaned;
      setPhoneNumber(formatPhoneDisplay(local));
    }
  }, [user]);

  const fetchFixture = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/fixtures/${fixtureId}`);
      setFixture(response.data);
    } catch (error) {
      console.error('Failed to fetch fixture:', error);
      toast.error('Fixture not found');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (delta) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  const handlePhoneChange = (e) => {
    // Only allow digits
    const value = e.target.value.replace(/[^\d\s]/g, '');
    setPhoneNumber(formatPhoneDisplay(value));
  };

  const handleBuyTickets = () => {
    if (!isAuthenticated) {
      sessionStorage.setItem('redirectAfterLogin', `/fixture/${fixtureId}`);
      toast.info('Please sign in to purchase tickets');
      navigate('/login');
      return;
    }
    setPaymentError(null);
    setIsCheckoutOpen(true);
  };

  const initiatePayment = async () => {
    if (!isValidGambianPhone(phoneNumber)) {
      toast.error('Please enter a valid Gambian phone number (e.g., 771 2345)');
      return;
    }

    const rawPhone = getRawPhoneNumber(phoneNumber);

    try {
      setProcessing(true);
      setPaymentError(null);

      const response = await axios.post(
        `${API_URL}/api/fixtures/${fixtureId}/purchase`,
        {
          quantity,
          phone_number: rawPhone,
          payment_method: 'modempay'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.checkout_url) {
        // Redirect to ModemPay checkout
        toast.success('Redirecting to payment...');
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (error) {
      console.error('Payment failed:', error);
      setPaymentError(error.response?.data?.detail || 'Failed to create payment. Please try again.');
      toast.error(error.response?.data?.detail || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  const totalPrice = fixture ? fixture.ticket_price * quantity : 0;
  const ticketsRemaining = fixture ? fixture.total_tickets - (fixture.tickets_sold || 0) : 0;
  const displayPhoneNumber = `+220 ${phoneNumber}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="w-8 h-8 text-[#0055FF] animate-spin" />
        </div>
      </div>
    );
  }

  if (!fixture) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="text-center py-40 px-4">
          <Trophy className="w-12 h-12 sm:w-16 sm:h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">Fixture Not Found</h3>
          <Link to="/football">
            <Button variant="outline" className="mt-4 border-white/20 text-white hover:bg-white/10">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Football
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-6 sm:py-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0055FF]/15 via-[#0055FF]/5 to-transparent" />
        
        <div className="relative max-w-5xl mx-auto px-4">
          {/* Back Button */}
          <Link to="/football" className="inline-flex items-center gap-1.5 text-gray-400 hover:text-white mb-4 sm:mb-6 text-sm transition-colors">
            <ChevronLeft className="w-4 h-4" />
            <span>Back</span>
          </Link>

          {/* Match Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden"
          >
            {/* Tournament Header */}
            <div className="bg-gradient-to-r from-[#0055FF]/20 to-purple-500/20 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">{fixture.tournament_flag || '🇬🇲'}</span>
                <span className="text-white text-sm font-medium">{fixture.tournament_name}</span>
              </div>
              {fixture.is_live && <LiveBadge />}
            </div>

            {/* Teams */}
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                {/* Home Team */}
                <div className="flex-1 text-center">
                  <div className="w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-2 sm:mb-3 rounded-full bg-white/10 flex items-center justify-center border border-white/20">
                    {fixture.home_logo ? (
                      <img src={fixture.home_logo} alt={fixture.home_team} className="w-10 h-10 sm:w-14 sm:h-14 object-contain" />
                    ) : (
                      <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-gray-500" />
                    )}
                  </div>
                  <h2 className="text-sm sm:text-lg font-bold text-white truncate px-1">{fixture.home_team}</h2>
                  <p className="text-gray-500 text-[10px] sm:text-xs mt-0.5">Home</p>
                </div>

                {/* VS */}
                <div className="px-2 sm:px-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#0055FF] to-purple-600 flex items-center justify-center shadow-lg shadow-[#0055FF]/20">
                    <span className="text-sm sm:text-lg font-bold text-white">VS</span>
                  </div>
                </div>

                {/* Away Team */}
                <div className="flex-1 text-center">
                  <div className="w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-2 sm:mb-3 rounded-full bg-white/10 flex items-center justify-center border border-white/20">
                    {fixture.away_logo ? (
                      <img src={fixture.away_logo} alt={fixture.away_team} className="w-10 h-10 sm:w-14 sm:h-14 object-contain" />
                    ) : (
                      <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-gray-500" />
                    )}
                  </div>
                  <h2 className="text-sm sm:text-lg font-bold text-white truncate px-1">{fixture.away_team}</h2>
                  <p className="text-gray-500 text-[10px] sm:text-xs mt-0.5">Away</p>
                </div>
              </div>

              {/* Match Info */}
              <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-5 text-gray-400 border-t border-white/10 pt-4">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-[#0055FF]" />
                  <span className="text-xs sm:text-sm">{fixture.date}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-[#0055FF]" />
                  <span className="text-xs sm:text-sm">{fixture.time}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-[#0055FF]" />
                  <span className="text-xs sm:text-sm truncate max-w-[120px] sm:max-w-none">{fixture.venue}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ticket Purchase Section */}
      <section className="py-6 sm:py-10">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Ticket Selection */}
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-4 sm:p-5">
                <h3 className="text-base sm:text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Ticket className="w-4 h-4 sm:w-5 sm:h-5 text-[#0055FF]" />
                  Select Tickets
                </h3>

                <div className="space-y-4">
                  {/* Ticket Type */}
                  <div className="p-3 sm:p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-semibold text-white text-sm sm:text-base">Standard</p>
                        <p className="text-xs text-gray-500">General admission</p>
                      </div>
                      <p className="text-xl sm:text-2xl font-bold text-[#0055FF]">D{fixture.ticket_price}</p>
                    </div>

                    {/* Quantity Selector */}
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Qty</span>
                      <div className="flex items-center gap-2 sm:gap-3">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleQuantityChange(-1)}
                          disabled={quantity <= 1}
                          className="h-8 w-8 sm:h-9 sm:w-9 border-white/20 text-white hover:bg-white/10"
                        >
                          <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                        <span className="text-lg sm:text-xl font-bold text-white w-6 text-center">{quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleQuantityChange(1)}
                          disabled={quantity >= 10 || quantity >= ticketsRemaining}
                          className="h-8 w-8 sm:h-9 sm:w-9 border-white/20 text-white hover:bg-white/10"
                        >
                          <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Availability */}
                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <span className="text-gray-400">Available</span>
                    <Badge variant={ticketsRemaining > 100 ? 'default' : 'destructive'} className="text-xs">
                      {ticketsRemaining} left
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Order Summary */}
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-4 sm:p-5">
                <h3 className="text-base sm:text-lg font-bold text-white mb-4">Summary</h3>

                <div className="space-y-3 mb-4">
                  <div className="flex justify-between text-gray-300 text-sm">
                    <span>Ticket × {quantity}</span>
                    <span>D{fixture.ticket_price * quantity}</span>
                  </div>
                  <div className="flex justify-between text-gray-300 text-sm">
                    <span>Fee</span>
                    <span className="text-green-400">FREE</span>
                  </div>
                  <div className="border-t border-white/10 pt-3 flex justify-between text-white">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-xl sm:text-2xl text-[#0055FF]">D{totalPrice}</span>
                  </div>
                </div>

                <Button
                  onClick={handleBuyTickets}
                  disabled={ticketsRemaining === 0}
                  className="w-full h-12 sm:h-14 text-sm sm:text-base bg-gradient-to-r from-[#0055FF] to-purple-600 hover:from-[#0066FF] hover:to-purple-500 shadow-lg shadow-[#0055FF]/20"
                >
                  <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  {ticketsRemaining === 0 ? 'Sold Out' : 'Buy Tickets'}
                </Button>

                <div className="flex items-center justify-center gap-1.5 mt-3 text-[10px] sm:text-xs text-gray-500">
                  <ShieldCheck className="w-3 h-3" />
                  <span>Secure payment via ModemPay</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Checkout Dialog */}
      <Dialog open={isCheckoutOpen} onOpenChange={(open) => { if (!processing) setIsCheckoutOpen(open); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-md mx-auto bg-[#121212] border-white/10 text-white p-4 sm:p-6 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Checkout</DialogTitle>
          </DialogHeader>

          <div className="py-2 space-y-4">
            {/* Order Summary */}
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Match</span>
                <span className="text-sm font-medium text-white">{fixture.home_team} vs {fixture.away_team}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Tickets</span>
                <span className="text-sm font-medium text-white">{quantity} × D{fixture.ticket_price}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <span className="text-xs text-gray-500">Total</span>
                <span className="text-lg font-bold text-[#0055FF]">D{totalPrice}</span>
              </div>
            </div>

            {/* Phone Number Input */}
            <div>
              <label className="text-xs sm:text-sm font-medium text-gray-300 mb-2 block">
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  <span className="text-lg">🇬🇲</span>
                  <span className="text-gray-400 text-sm font-medium">+220</span>
                </div>
                <Input
                  type="tel"
                  inputMode="numeric"
                  placeholder="Enter your number"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  className="pl-24 h-12 bg-white/5 border-white/10 text-white text-lg font-medium tracking-wider focus:border-[#0055FF]/50 placeholder:text-gray-600 placeholder:font-normal"
                  maxLength={8}
                  disabled={processing}
                />
              </div>
              
              {/* Phone Number Preview */}
              {phoneNumber && (
                <div className="mt-2 p-2 rounded-lg bg-[#0055FF]/10 border border-[#0055FF]/20">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#0055FF]" />
                    <span className="text-sm text-white font-medium tracking-wide">
                      {displayPhoneNumber}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1 ml-6">
                    You'll complete payment on ModemPay
                  </p>
                </div>
              )}
            </div>

            {/* Payment Method Info */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-[#0055FF]/10 to-purple-500/10 border border-[#0055FF]/20">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-[#0055FF]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-white text-sm">Secure Payment</p>
                <p className="text-[10px] text-gray-500">Wave, Orange Money & more</p>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-500" />
            </div>

            {/* Error Message */}
            {paymentError && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-400">{paymentError}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setIsCheckoutOpen(false)}
                className="order-2 sm:order-1 border-white/20 hover:bg-white/10"
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                onClick={initiatePayment}
                disabled={processing || !phoneNumber || phoneNumber.replace(/\s/g, '').length < 7}
                className="order-1 sm:order-2 flex-1 sm:flex-initial h-12 bg-gradient-to-r from-[#0055FF] to-purple-600 hover:from-[#0066FF] hover:to-purple-500"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay D{totalPrice}
                  </>
                )}
              </Button>
            </div>

            {/* Security Note */}
            <div className="text-center text-[10px] text-gray-600 pt-2">
              <ShieldCheck className="w-3 h-3 inline mr-1" />
              You'll be redirected to ModemPay's secure checkout
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="py-8 sm:py-10 bg-[#0a0a0a] border-t border-white/[0.05]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-600 text-xs">© 2026 Gam Ticket. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
